<template>
  <AIVDialog
    v-model="isVisible"
    title="Approve Shift"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal approve-shift-modal"
  >
    <div class="approve-shift-content">
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p>
          <strong>Date:</strong>
          {{ shift && shift.date ? formatDate(shift.date) : "Loading..." }}
        </p>
        <p>
          <strong>Scheduled Time:</strong>
          {{
            formatTimeRange(
              shift?.scheduledStartTime ||
                shift?.originalData?.scheduled_start ||
                shift?.startTime ||
                shift?.scheduled_start,
              shift?.scheduledEndTime ||
                shift?.originalData?.scheduled_end ||
                shift?.endTime ||
                shift?.scheduled_end,
              shift?.date,
            )
          }}
        </p>
        <p
          v-if="
            !isGroupShift &&
            shift &&
            (shift.originalData?.clockIn ||
              shift.originalData?.clock_in ||
              shift.originalData?.clockOut ||
              shift.originalData?.clock_out ||
              shift.actualStartTime ||
              shift.actualEndTime)
          "
        >
          <strong>Actual Time:</strong>
          {{ formatActualTimeRange(shift) }}
        </p>
        <p v-if="!isGroupShift && clockInGeocode" class="clock-location-row">
          <strong>Clock In Location:</strong>
          {{ clockLocationLabel(clockInGeocode) }}
          <a
            :href="clockMapUrl(clockInGeocode)"
            target="_blank"
            rel="noopener"
            class="clock-map-link"
          ><i class="fa-solid fa-map-pin"></i> Map</a>
        </p>
        <p v-if="!isGroupShift && clockOutGeocode" class="clock-location-row">
          <strong>Clock Out Location:</strong>
          {{ clockLocationLabel(clockOutGeocode) }}
          <a
            :href="clockMapUrl(clockOutGeocode)"
            target="_blank"
            rel="noopener"
            class="clock-map-link"
          ><i class="fa-solid fa-map-pin"></i> Map</a>
        </p>
        <p v-if="!isGroupShift && shift && totalHours">
          <strong>Total Time:</strong> {{ totalHours }}
        </p>
        <!-- Shadow/training hours — informational only, never billed -->
        <div v-if="sideCaregiverHours.length > 0" class="side-caregiver-hours">
          <p
            v-for="entry in sideCaregiverHours"
            :key="'side-hours-' + entry.id"
            class="side-caregiver-hours-row"
          >
            <strong>{{ entry.label }}:</strong>
            {{ entry.hours }}
            <span class="side-hours-note">(not billed)</span>
          </p>
        </div>
        <template v-if="hasBreaks">
          <p v-if="breakTimeFormatted">
            <strong>Break Time:</strong> {{ breakTimeFormatted }}
          </p>
          <p v-if="totalBreakTimeFormatted">
            <strong>Total Break Time:</strong> {{ totalBreakTimeFormatted }}
          </p>
          <p v-if="totalBillableTimeFormatted">
            <strong>Total Billable Time:</strong> {{ totalBillableTimeFormatted }}
          </p>
        </template>
        <p
          v-if="
            shift?.payer !== 'Non Service' &&
            shift?.originalData?.payer !== 'Non Service'
          "
        >
          <strong>Client:</strong>
          {{ isGroupShift && groupClients.length > 1
            ? groupClients.map(c => c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()).join(', ')
            : shift ? formatClient(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Caregiver:</strong>
          {{ isGroupShift && groupCaregivers.length > 1
            ? groupCaregivers.map(c => c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim()).join(', ')
            : shift ? formatCaregiver(shift) : "Loading..." }}
        </p>
        <p>
          <strong>Payer:</strong>
          {{ shift?.payer || shift?.originalData?.payer || "Not specified" }}
        </p>
        <p v-if="shift">
          <strong>Service Code:</strong> {{ formatServiceCode(shift) }}
        </p>
        <p v-if="shift && (shift.shift_type || shift.shiftType)">
          <strong>Shift Type:</strong>
          {{ shift.shift_type || shift.shiftType || "N/A" }}
        </p>
        <p>
          <strong>Status:</strong>
          <span
            v-if="shift && (shift.status || shift.shift_status)"
            :class="[
              'status-badge',
              getStatusClass(shift.status || shift.shift_status),
            ]"
            >{{ formatStatus(shift.status || shift.shift_status) }}</span
          >
          <span v-else>Loading...</span>
        </p>
      </div>

      <!-- Caregiver Clock Times Section (Group Shifts) -->
      <div class="mb-4" v-if="isGroupShift && shift && groupCaregivers.length">
        <h5>Caregiver Clock Times</h5>
        <div class="caregiver-clock-times">
          <div
            v-for="caregiver in groupCaregivers"
            :key="caregiver.id"
            class="caregiver-clock-row"
          >
            <div class="caregiver-name">
              {{ caregiver.name || `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() }}
            </div>
            <div class="caregiver-times">
              <span class="clock-label">In:</span>
              <span class="clock-value">
                {{ getCaregiverClockIn(caregiver.id) || 'Not clocked in' }}
              </span>
              <span class="clock-label">Out:</span>
              <span class="clock-value">
                {{ getCaregiverClockOut(caregiver.id) || 'Not clocked out' }}
              </span>
            </div>
            <div class="caregiver-duration" v-if="getCaregiverDuration(caregiver.id)">
              {{ getCaregiverDuration(caregiver.id) }}
            </div>
            <div
              v-if="getCaregiverGeocode(caregiver.id, 'in') || getCaregiverGeocode(caregiver.id, 'out')"
              class="caregiver-locations"
            >
              <div v-if="getCaregiverGeocode(caregiver.id, 'in')" class="clock-location-row">
                <span class="clock-label">In location:</span>
                {{ clockLocationLabel(getCaregiverGeocode(caregiver.id, 'in')) }}
                <a
                  :href="clockMapUrl(getCaregiverGeocode(caregiver.id, 'in'))"
                  target="_blank"
                  rel="noopener"
                  class="clock-map-link"
                ><i class="fa-solid fa-map-pin"></i> Map</a>
              </div>
              <div v-if="getCaregiverGeocode(caregiver.id, 'out')" class="clock-location-row">
                <span class="clock-label">Out location:</span>
                {{ clockLocationLabel(getCaregiverGeocode(caregiver.id, 'out')) }}
                <a
                  :href="clockMapUrl(getCaregiverGeocode(caregiver.id, 'out'))"
                  target="_blank"
                  rel="noopener"
                  class="clock-map-link"
                ><i class="fa-solid fa-map-pin"></i> Map</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Tasks Completed Section (Group Shifts) -->
      <div class="mb-4" v-if="isGroupShift && shift && groupClientDetails.length">
        <h5>Client Tasks Completed</h5>
        <div class="client-tasks-list">
          <div
            v-for="clientDetail in groupClientDetails"
            :key="clientDetail.id"
            class="client-task-card"
          >
            <div class="client-task-header">
              {{ getClientDetailName(clientDetail) }}
            </div>
            <div class="client-task-rows">
              <div v-if="clientDetail.did_not_attend" class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-user-xmark" style="color: #f56c6c;"></i>
                  <span class="client-task-label" style="color: #f56c6c;">Did Not Attend</span>
                </div>
                <span class="client-task-pending" style="color: #f56c6c;">All tasks skipped</span>
              </div>
              <template v-else>
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-car-side"></i>
                  <span class="client-task-label">Pick Up</span>
                </div>
                <span v-if="clientDetail.pickup_time" class="client-task-completed">
                  {{ getTaskCaregiverName(clientDetail.pickup_caregiver_id) }}
                  - {{ formatClockTime(clientDetail.pickup_time) }}
                </span>
                <span v-else class="client-task-pending">Not completed</span>
              </div>
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-location-dot"></i>
                  <span class="client-task-label">Drop Off</span>
                </div>
                <span v-if="clientDetail.dropoff_time" class="client-task-completed">
                  {{ getTaskCaregiverName(clientDetail.dropoff_caregiver_id) }}
                  - {{ formatClockTime(clientDetail.dropoff_time) }}
                </span>
                <span v-else class="client-task-pending">Not completed</span>
              </div>
              <div class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-clipboard"></i>
                  <span class="client-task-label">Shift Notes</span>
                </div>
                <span v-if="clientDetail.shift_notes_completed" class="client-task-completed">
                  {{ getTaskCaregiverName(clientDetail.shift_notes_caregiver_id) }}
                  - Completed
                </span>
                <span v-else class="client-task-pending">Not completed</span>
              </div>
              <div v-if="clientDetail.medications_caregiver_id" class="client-task-row">
                <div class="client-task-info">
                  <i class="fa-solid fa-pills"></i>
                  <span class="client-task-label">Medications</span>
                </div>
                <span v-if="clientDetail.medications_completed" class="client-task-completed">
                  {{ getTaskCaregiverName(clientDetail.medications_caregiver_id) }}
                  - Completed
                </span>
                <span v-else class="client-task-pending">Not completed</span>
              </div>
              </template>
              <!-- Per-client shift note details -->
              <div v-if="clientDetail.shift_notes_completed || clientDetail.shift_notes" class="client-notes-details">
                <div v-if="parseActivities(clientDetail.ls_activities).length" class="client-notes-field">
                  <strong>Life Skills Activities:</strong>
                  <div class="activity-tags">
                    <span v-for="(activity, idx) in parseActivities(clientDetail.ls_activities)" :key="'ls-'+idx" class="activity-tag">{{ activity }}</span>
                  </div>
                </div>
                <div v-if="parseActivities(clientDetail.sed_activities).length" class="client-notes-field">
                  <strong>Supported Education Activities:</strong>
                  <div class="activity-tags">
                    <span v-for="(activity, idx) in parseActivities(clientDetail.sed_activities)" :key="'sed-'+idx" class="activity-tag">{{ activity }}</span>
                  </div>
                </div>
                <div v-if="parseActivities(clientDetail.sb_activities).length" class="client-notes-field">
                  <strong>Skills Building Activities:</strong>
                  <div class="activity-tags">
                    <span v-for="(activity, idx) in parseActivities(clientDetail.sb_activities)" :key="'sb-'+idx" class="activity-tag">{{ activity }}</span>
                  </div>
                </div>
                <div v-if="clientDetail.jd_activity" class="client-notes-field">
                  <strong>Job Development Activity:</strong>
                  <p class="notes-text">{{ clientDetail.jd_activity }}</p>
                </div>
                <div v-if="clientDetail.jd_outcome" class="client-notes-field">
                  <strong>Outcome:</strong>
                  <p class="notes-text">{{ clientDetail.jd_outcome }}</p>
                </div>
                <div v-if="clientDetail.jd_next_steps" class="client-notes-field">
                  <strong>Next Steps:</strong>
                  <p class="notes-text">{{ clientDetail.jd_next_steps }}</p>
                </div>
                <div v-if="parseActivities(clientDetail.jc_primary_activities).length" class="client-notes-field">
                  <strong>Primary Job Coaching Activities:</strong>
                  <div class="activity-tags">
                    <span v-for="(activity, idx) in parseActivities(clientDetail.jc_primary_activities)" :key="'jcp-'+idx" class="activity-tag primary-activity">{{ activity }}</span>
                  </div>
                </div>
                <div v-if="parseActivities(clientDetail.jc_secondary_activities).length" class="client-notes-field">
                  <strong>Secondary Job Coaching Activities:</strong>
                  <div class="activity-tags">
                    <span v-for="(activity, idx) in parseActivities(clientDetail.jc_secondary_activities)" :key="'jcs-'+idx" class="activity-tag secondary-activity">{{ activity }}</span>
                  </div>
                </div>
                <!-- Service Code Questions -->
                <div v-if="getClientQuestionAnswers(clientDetail).length" class="client-notes-field">
                  <div v-for="qa in getClientQuestionAnswers(clientDetail)" :key="'qa-'+qa.questionId" class="question-answer-row">
                    <div class="question-text">{{ qa.questionText }}</div>
                    <div class="answer-text" :class="{ 'answer-yes': qa.answer === 'yes', 'answer-no': qa.answer === 'no' }">
                      {{ qa.answer || '(no answer)' }}
                    </div>
                  </div>
                </div>
                <!-- Client Goal Responses -->
                <div v-if="getClientGoalAnswers(clientDetail).length" class="client-notes-field">
                  <div v-for="ga in getClientGoalAnswers(clientDetail)" :key="'ga-'+ga.goalId" class="question-answer-row">
                    <div class="question-text">{{ ga.goalText }}</div>
                    <div class="answer-text" :class="{ 'answer-yes': ga.answer === 'yes', 'answer-no': ga.answer === 'no' }">
                      {{ ga.answer || '(no answer)' }}
                    </div>
                  </div>
                </div>
                <!-- General Concerns -->
                <div v-if="clientDetail.shift_notes" class="client-notes-field question-answer-row">
                  <div class="question-text">Do you have any concerns about this individual today?</div>
                  <div class="answer-text">{{ clientDetail.shift_notes }}</div>
                </div>
              </div>
              <!-- Medication Administration Details -->
              <div v-if="getClientMedicationLogs(clientDetail).length" class="client-notes-details">
                <strong>Medication Administration</strong>
                <div
                  v-for="log in getClientMedicationLogs(clientDetail)"
                  :key="'med-' + log.id"
                  class="client-notes-field question-answer-row"
                >
                  <div class="question-text">{{ log.medication ? log.medication.name : 'Unknown Medication' }}</div>
                  <div class="answer-text" :class="{ 'answer-yes': log.was_administered, 'answer-no': !log.was_administered }">
                    {{ log.was_administered ? 'Given' : 'Skipped' }}
                    <span v-if="log.skip_reason"> - {{ log.skip_reason }}</span>
                    <span v-if="log.dosage_given"> ({{ log.dosage_given }})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mileage Tracking Section (readonly) -->
      <div class="mb-4" v-if="shiftHasTransport">
        <h5><i class="fa-solid fa-car mr-1"></i> Mileage Tracking</h5>
        <div class="mileage-summary">
          <p v-if="mileageInfo.start_location">
            <strong>Start Location:</strong> {{ mileageInfo.start_location }}
          </p>
          <p v-if="mileageInfo.mid_location">
            <strong>Places Visited:</strong> {{ mileageInfo.mid_location }}
          </p>
          <p v-if="mileageInfo.end_location">
            <strong>End Location:</strong> {{ mileageInfo.end_location }}
          </p>
          <p v-if="mileageInfo.odometer_start != null">
            <strong>Odometer Start:</strong> {{ mileageInfo.odometer_start }}
          </p>
          <p v-if="mileageInfo.odometer_end != null">
            <strong>Odometer End:</strong> {{ mileageInfo.odometer_end }}
          </p>
          <p v-if="mileageInfo.total_miles != null">
            <strong>Total Miles:</strong> {{ mileageInfo.total_miles }} miles
          </p>
        </div>

        <!-- Mileage approval: only approved miles reach payroll/reports.
             Pre-checked; the number can be corrected before approving. -->
        <div v-if="mileageTripRows.length > 0" class="mileage-approval mt-2">
          <div
            v-for="row in mileageTripRows"
            :key="'mtrip-' + row.key"
            class="mileage-approval-row"
          >
            <el-checkbox v-model="row.approved">
              Approve mileage{{ row.label ? ` — ${row.label}` : "" }}
              (recorded {{ row.recorded }} mi)
            </el-checkbox>
            <div v-if="row.approved" class="mileage-approval-miles">
              <label>Miles to reimburse:</label>
              <el-input
                v-model="row.miles"
                type="number"
                size="small"
                class="mileage-miles-input"
                :min="0"
                :step="0.1"
              />
              <span
                v-if="parseFloat(row.miles) !== row.recorded"
                class="mileage-adjusted-note"
              >
                adjusted from {{ row.recorded }} mi
              </span>
            </div>
            <div v-else class="mileage-held-note">
              These miles will be held — they won't be reimbursed until the
              shift approval is redone with mileage approved.
            </div>
            <div
              v-if="allowanceFor(row)"
              class="mileage-allowance"
              :class="allowanceClass(row)"
            >
              <i class="fa-solid fa-gauge-high"></i>
              Allowance {{ allowancePeriodLabel(row) }}:
              ${{ allowanceFor(row).approvedDollars.toFixed(2) }} used<template
                v-if="allowancePendingOther(row) > 0"
              >
                + ${{ allowancePendingOther(row).toFixed(2) }} pending</template
              >
              of ${{ allowanceFor(row).capDollars.toFixed(2) }}
              <template v-if="row.approved">
                · this approval adds ≈ ${{ allowanceAdds(row).toFixed(2) }}
              </template>
              <strong v-if="allowanceOverBy(row) > 0">
                — exceeds the allowance by ${{ allowanceOverBy(row).toFixed(2) }}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Generic VR activity pickers (LS/SED/SB and any other VR code with an
           admin-managed activity list) -->
      <template v-if="shift && !isGroupShift">
        <div
          v-for="code in vrCodesWithActivities"
          :key="'vract-' + code"
          class="mb-4"
        >
          <h5>
            {{ vrActivityState[code].name }} Activities Completed
            <small v-if="maxForCode(code) != null">(Select up to {{ maxForCode(code) }})</small>
          </h5>
          <AIVSelect
            :value="vrActivityState[code].selectedIds"
            @input="onVrActivitySelect(code, $event)"
            :options="vrActivityState[code].options"
            placeholder="Select activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
          />
        </div>
      </template>

      <!-- Legacy Job Development fields (CE/SE/SJBT/TWE): shown only for
           shifts already documented on the jd_* columns, or when the code
           has no configured questions (see showJdFields) -->
      <div v-if="shift && showJdFields && !isGroupShift">
        <div class="mb-4">
          <h5>Job Development Activity Completed</h5>
          <el-input
            v-model="editableJDActivity"
            type="textarea"
            :rows="4"
            placeholder="Enter job development activity..."
          />
        </div>

        <div class="mb-4">
          <h5>Outcome of Activity</h5>
          <el-input
            v-model="editableJDOutcome"
            type="textarea"
            :rows="4"
            placeholder="Enter outcome of activity..."
          />
        </div>

        <div class="mb-4">
          <h5>Next Steps Forward</h5>
          <el-input
            v-model="editableJDNextSteps"
            type="textarea"
            :rows="4"
            placeholder="Enter next steps..."
          />
        </div>
      </div>

      <!-- JC Service Section -->
      <div v-if="shift && isJCService && !isGroupShift">
        <div class="mb-4">
          <h5>Primary Job Coaching Activities (Select up to 3)</h5>
          <AIVSelect
            v-model="selectedJCPrimaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select primary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>

        <div class="mb-4">
          <h5>Secondary Job Coaching Activities (Select up to 3)</h5>
          <AIVSelect
            v-model="selectedJCSecondaryActivities"
            :options="jobCoachingActivities"
            placeholder="Select secondary activities"
            :multi-select="true"
            :searchable="true"
            :custom-style="{ width: '100%' }"
            :loading="loadingJobCoachingActivities"
          />
        </div>
      </div>

      <!-- DSPD 1-on-1 Shift Notes (Questions, Goals, Concerns) — editable -->
      <div class="mb-4" v-if="shift && isDSPDOneOnOneShift">
        <h5>Shift Notes</h5>

        <!-- Service Code Questions (editable) -->
        <div
          v-for="q in visibleDspdQuestions"
          :key="'dspd1on1-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" style="color: #f56c6c;">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="editableQuestionAnswers[q.id] || ''"
            @input="$set(editableQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="editableQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(editableQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="editableQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(editableQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <el-select
            v-else-if="q.question_type === 'select'"
            :value="editableQuestionAnswers[q.id] || ''"
            @input="$set(editableQuestionAnswers, q.id, $event)"
            placeholder="Select an option"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
          <el-select
            v-else-if="q.question_type === 'multi_select'"
            multiple
            :value="parseMultiAnswer(editableQuestionAnswers[q.id])"
            @input="$set(editableQuestionAnswers, q.id, joinMultiAnswer($event))"
            placeholder="Select all that apply"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>

        <!-- Answers to questions no longer on the service code (read-only) -->
        <div
          v-for="qa in staleQuestionAnswerRows"
          :key="'dspd1on1-qa-' + qa.questionId"
          class="client-notes-field question-answer-row"
        >
          <div class="question-text">{{ qa.questionText }}</div>
          <div class="answer-text" :class="{ 'answer-yes': qa.answer === 'yes', 'answer-no': qa.answer === 'no' }">
            {{ qa.answer || '(no answer)' }}
          </div>
        </div>

        <!-- Client Goals (editable) -->
        <div
          v-for="goal in editableClientGoals"
          :key="'dspd1on1-g-' + goal.id"
          class="mb-3"
        >
          <label class="form-label">{{ goal.question_text }}</label>
          <el-input
            v-if="goal.question_type === 'free_response' || !goal.question_type"
            :value="editableGoalAnswers[goal.id] || ''"
            @input="$set(editableGoalAnswers, goal.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="goal.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="editableGoalAnswers[goal.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(editableGoalAnswers, goal.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="editableGoalAnswers[goal.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(editableGoalAnswers, goal.id, 'no')"
            >No</el-button>
          </div>
        </div>

        <!-- Answers to goals no longer on the client (read-only) -->
        <div
          v-for="ga in staleGoalAnswerRows"
          :key="'dspd1on1-ga-' + ga.goalId"
          class="client-notes-field question-answer-row"
        >
          <div class="question-text">{{ ga.goalText }}</div>
          <div class="answer-text" :class="{ 'answer-yes': ga.answer === 'yes', 'answer-no': ga.answer === 'no' }">
            {{ ga.answer || '(no answer)' }}
          </div>
        </div>

        <!-- Concerns (editable; legacy shifts and question-less codes only) -->
        <div v-if="showConcernsField" class="mb-3">
          <label class="form-label">Do you have any concerns about this individual today?</label>
          <el-input
            v-model="editableShiftNotes"
            type="textarea"
            :rows="3"
            placeholder="Enter your answer..."
          />
        </div>
      </div>

      <!-- Medication Administration (DSPD 1-on-1 Shifts) -->
      <div class="mb-4" v-if="isDSPDOneOnOneShift && dspdOneOnOneMedicationLogs.length > 0">
        <h5>Medication Administration</h5>
        <div
          v-for="log in dspdOneOnOneMedicationLogs"
          :key="'med-1on1-' + log.id"
          class="client-notes-field question-answer-row"
        >
          <div class="question-text">{{ log.medication ? log.medication.name : 'Unknown Medication' }}</div>
          <div class="answer-text" :class="{ 'answer-yes': log.was_administered, 'answer-no': !log.was_administered }">
            {{ log.was_administered ? 'Given' : 'Skipped' }}
            <span v-if="log.skip_reason"> - {{ log.skip_reason }}</span>
            <span v-if="log.dosage_given"> ({{ log.dosage_given }})</span>
          </div>
        </div>
      </div>

      <!-- VR / service-code questions (editable) -->
      <div
        class="mb-4"
        v-if="shift && !isGroupShift && !isDSPDOneOnOneShift && !showJdFields && (serviceCodeQuestions.length || vrQuestionAnswers.length)"
      >
        <h5>Shift Note Questions</h5>
        <div
          v-for="q in serviceCodeQuestions"
          :key="'vr-q-' + q.id"
          class="mb-3"
        >
          <label class="form-label">
            {{ q.question_text }}
            <span v-if="q.required" style="color: #f56c6c;">*</span>
          </label>
          <div
            v-if="q.description"
            class="shift-notes-description mb-2"
            style="white-space: pre-line;"
          >{{ q.description }}</div>
          <el-input
            v-if="q.question_type === 'free_response' || !q.question_type"
            :value="editableQuestionAnswers[q.id] || ''"
            @input="$set(editableQuestionAnswers, q.id, $event)"
            type="textarea"
            :rows="2"
            placeholder="Enter your answer..."
          />
          <div v-else-if="q.question_type === 'yes_no'" class="yes-no-buttons">
            <el-button
              :type="editableQuestionAnswers[q.id] === 'yes' ? 'primary' : 'default'"
              size="medium"
              @click="$set(editableQuestionAnswers, q.id, 'yes')"
            >Yes</el-button>
            <el-button
              :type="editableQuestionAnswers[q.id] === 'no' ? 'danger' : 'default'"
              size="medium"
              @click="$set(editableQuestionAnswers, q.id, 'no')"
            >No</el-button>
          </div>
          <el-select
            v-else-if="q.question_type === 'select'"
            :value="editableQuestionAnswers[q.id] || ''"
            @input="$set(editableQuestionAnswers, q.id, $event)"
            placeholder="Select an option"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
          <el-select
            v-else-if="q.question_type === 'multi_select'"
            multiple
            :value="parseMultiAnswer(editableQuestionAnswers[q.id])"
            @input="$set(editableQuestionAnswers, q.id, joinMultiAnswer($event))"
            placeholder="Select all that apply"
            style="width: 100%;"
          >
            <el-option
              v-for="(opt, i) in (q.options || [])"
              :key="i"
              :label="opt"
              :value="opt"
            />
          </el-select>
        </div>

        <!-- Answers to questions no longer on the service code (read-only) -->
        <div
          v-for="qa in staleQuestionAnswerRows"
          :key="'vr-qa-' + qa.questionId"
          class="client-notes-field question-answer-row"
        >
          <div class="question-text">{{ qa.questionText }}</div>
          <div
            class="answer-text"
            :class="{ 'answer-yes': qa.answer === 'yes', 'answer-no': qa.answer === 'no' }"
          >
            {{ qa.answer || '(no answer)' }}
          </div>
        </div>
      </div>

      <!-- Non-DSPD, Non-JD, Non-Group Shift Notes.
           Hidden when admin-configured shift-note questions apply — the
           question answers are reviewed read-only above instead. -->
      <div
        class="mb-4"
        v-if="shift && !isJDService && !isGroupShift && !isDSPDOneOnOneShift && !hasShiftNoteQuestions"
      >
        <h5>Shift Notes</h5>
        <el-input
          v-model="editableShiftNotes"
          type="textarea"
          :rows="6"
          placeholder="Edit shift notes..."
        />
      </div>
    </div>

      <!-- Auth hours/units warning -->
      <div v-if="authWarning" class="auth-warning-banner">
        <div class="alert alert-warning">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>{{ authWarningMessage }}</span>
        </div>
        <el-checkbox v-model="authAcknowledged" class="auth-acknowledge-checkbox">
          I acknowledge that this shift will exceed the authorization's remaining hours/units
        </el-checkbox>
      </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">Cancel</el-button>
        <el-button type="success" @click="handleApprove" :loading="approving">
          <i class="fa-solid fa-check"></i> Approve Shift
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { authorizationsService } from "@/serviceHandlers/authorizationsService";
import { jobCoachingActivitiesService } from "@/serviceHandlers/jobCoachingActivitiesService";
import vrActivitiesMixin from "@/mixins/vrActivitiesMixin";
import clockLocationMixin from "@/mixins/clockLocationMixin";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { useLoginStore } from "@/stores/loginStore";
import { isDSPDGroupShift } from "@/utils/shiftStatus";

export default {
  name: "ApproveShiftModal",
  components: { AIVDialog, AIVSelect },
  mixins: [vrActivitiesMixin, clockLocationMixin],

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      approving: false,
      authWarning: false,
      authWarningMessage: "",
      authAcknowledged: false,
      // One row per recorded trip (shift-level + per-caregiver clock rows):
      // { key, id_users, label, recorded, approved, miles }
      mileageTripRows: [],
      // Allowance usage per caregiver id (from /shifts/mileage-allowance)
      mileageAllowances: {},
      approvalNotes: "",
      editableShiftNotes: "",
      fullShiftData: null,
      selectedLSActivities: [],
      selectedSEDActivities: [],
      selectedSBActivities: [],
      editableJDActivity: "",
      editableJDOutcome: "",
      editableJDNextSteps: "",
      selectedJCPrimaryActivities: [],
      selectedJCSecondaryActivities: [],
      jobCoachingActivities: [],
      loadingJobCoachingActivities: false,
      serviceCodeQuestions: [],
      clientGoals: {},
      // Editable shift-note question / goal answers (1-on-1 shifts), keyed by
      // question id / goal id. multi_select answers are comma-joined strings.
      editableQuestionAnswers: {},
      editableGoalAnswers: {},
      medicationLogs: {}, // Map of clientId to medication administration logs
      // LS / SED / SB activities are admin-managed in Settings and loaded from
      // the vr_activities table (see fetchVRActivities).
      lsActivities: [],
      sedActivities: [],
      sbActivities: [],
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    totalHours() {
      if (!this.shift) return null;

      const clockInTime =
        this.shift.originalData?.clockIn ||
        this.shift.originalData?.clock_in ||
        this.shift.actualStartTime;

      const clockOutTime =
        this.shift.originalData?.clockOut ||
        this.shift.originalData?.clock_out ||
        this.shift.actualEndTime;

      if (!clockInTime || !clockOutTime) return null;

      try {
        const clockIn = new Date(clockInTime);
        const clockOut = new Date(clockOutTime);

        if (isNaN(clockIn.getTime()) || isNaN(clockOut.getTime())) return null;

        let diffMs = clockOut.getTime() - clockIn.getTime();

        if (diffMs < 0) {
          const nextDayClockOut = new Date(clockOut);
          nextDayClockOut.setDate(nextDayClockOut.getDate() + 1);
          diffMs = nextDayClockOut.getTime() - clockIn.getTime();
        }

        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        if (hours === 0) {
          return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
        } else if (minutes === 0) {
          return `${hours} hour${hours !== 1 ? "s" : ""}`;
        } else {
          return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
        }
      } catch (error) {
        return null;
      }
    },

    // Shadow/training caregivers' own clocked hours — shown to the
    // approving admin for transparency; they never affect billing
    sideCaregiverHours() {
      const data = this.shift?.originalData || this.shift || {};
      if ((data.shift_type || "1-on-1") === "Group") return [];
      const caregivers = data.caregivers || [];
      const clockTimes = data.caregiverClockTimes || [];
      return caregivers
        .filter((c) => (c.role || "primary") !== "primary")
        .map((c) => {
          const ct = clockTimes.find(
            (x) => Number(x.id_users) === Number(c.id),
          );
          let hours = "no clock times";
          if (ct && ct.clock_in && ct.clock_out) {
            const h =
              (new Date(ct.clock_out) - new Date(ct.clock_in)) / 3600000;
            hours = `${h.toFixed(2)} hours`;
          } else if (ct && ct.clock_in) {
            hours = "still clocked in";
          }
          const role = c.role.charAt(0).toUpperCase() + c.role.slice(1);
          const name =
            c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
          return { id: c.id, label: `${name} (${role})`, hours };
        });
    },
    hasBreaks() {
      if (!this.shift) return false;
      const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
      // Check if there are any completed breaks (with both start and end time)
      return breaks.some(b => b.break_start && b.break_end);
    },

    breakTimeFormatted() {
      if (!this.shift) return null;
      const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
      const completedBreaks = breaks.filter(b => b.break_start && b.break_end);

      if (completedBreaks.length === 0) return null;

      // Format each break as a time range
      const breakRanges = completedBreaks.map(b => {
        const start = this.formatClockTime(b.break_start);
        const end = this.formatClockTime(b.break_end);
        return `${start} - ${end}`;
      });

      return breakRanges.join(', ');
    },

    totalBreakTimeFormatted() {
      if (!this.shift) return null;
      const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
      const completedBreaks = breaks.filter(b => b.break_start && b.break_end);

      if (completedBreaks.length === 0) return null;

      // Calculate total break time in milliseconds
      let totalBreakMs = 0;
      for (const breakRecord of completedBreaks) {
        const breakStart = new Date(breakRecord.break_start);
        const breakEnd = new Date(breakRecord.break_end);

        if (!isNaN(breakStart.getTime()) && !isNaN(breakEnd.getTime())) {
          totalBreakMs += breakEnd.getTime() - breakStart.getTime();
        }
      }

      const totalBreakMinutes = Math.floor(totalBreakMs / (1000 * 60));
      const hours = Math.floor(totalBreakMinutes / 60);
      const minutes = totalBreakMinutes % 60;

      if (hours === 0) {
        return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
      } else if (minutes === 0) {
        return `${hours} hour${hours !== 1 ? "s" : ""}`;
      } else {
        return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
      }
    },

    totalBillableTimeFormatted() {
      if (!this.shift) return null;

      const clockInTime =
        this.shift.originalData?.clockIn ||
        this.shift.originalData?.clock_in ||
        this.shift.actualStartTime;

      const clockOutTime =
        this.shift.originalData?.clockOut ||
        this.shift.originalData?.clock_out ||
        this.shift.actualEndTime;

      if (!clockInTime || !clockOutTime) return null;

      try {
        const clockIn = new Date(clockInTime);
        const clockOut = new Date(clockOutTime);

        if (isNaN(clockIn.getTime()) || isNaN(clockOut.getTime())) return null;

        let diffMs = clockOut.getTime() - clockIn.getTime();

        if (diffMs < 0) {
          const nextDayClockOut = new Date(clockOut);
          nextDayClockOut.setDate(nextDayClockOut.getDate() + 1);
          diffMs = nextDayClockOut.getTime() - clockIn.getTime();
        }

        // Calculate total break time in milliseconds
        const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
        const completedBreaks = breaks.filter(b => b.break_start && b.break_end);

        let totalBreakMs = 0;
        for (const breakRecord of completedBreaks) {
          const breakStart = new Date(breakRecord.break_start);
          const breakEnd = new Date(breakRecord.break_end);

          if (!isNaN(breakStart.getTime()) && !isNaN(breakEnd.getTime())) {
            totalBreakMs += breakEnd.getTime() - breakStart.getTime();
          }
        }

        // Subtract break time from total time
        const billableMs = diffMs - totalBreakMs;
        const billableMinutes = Math.floor(billableMs / (1000 * 60));
        const hours = Math.floor(billableMinutes / 60);
        const minutes = billableMinutes % 60;

        if (hours === 0) {
          return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
        } else if (minutes === 0) {
          return `${hours} hour${hours !== 1 ? "s" : ""}`;
        } else {
          return `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${minutes !== 1 ? "s" : ""}`;
        }
      } catch (error) {
        return null;
      }
    },

    isGroupShift() {
      if (!this.shift) return false;
      return isDSPDGroupShift(this.shift) || isDSPDGroupShift(this.shift?.originalData);
    },

    shiftHasTransport() {
      if (!this.shift) return false;
      const s = this.shift;
      const o = s.originalData || {};
      if (s.is_transporting || o.is_transporting) return true;
      // Mileage without transport (deductible driving) still shows here
      if (s.odometer_start != null || o.odometer_start != null) return true;
      // For group shifts, transport may live on per-caregiver clock times
      const clockTimes = s.caregiverClockTimes || o.caregiverClockTimes || [];
      if (Array.isArray(clockTimes) && clockTimes.some((ct) => ct.is_transporting || ct.odometer_start)) {
        return true;
      }
      return false;
    },

    mileageInfo() {
      const s = this.shift || {};
      const o = s.originalData || {};
      const pick = (k) => s[k] ?? o[k] ?? null;
      let info = {
        start_location: pick("mileage_start_location"),
        mid_location: pick("mileage_mid_location"),
        end_location: pick("mileage_end_location"),
        odometer_start: pick("odometer_start"),
        odometer_end: pick("odometer_end"),
      };
      // Merge from per-caregiver clock times (group shifts)
      const clockTimes = s.caregiverClockTimes || o.caregiverClockTimes || [];
      if (Array.isArray(clockTimes) && clockTimes.length) {
        const ct = clockTimes.find((c) => c.is_transporting || c.odometer_start || c.odometer_end) || clockTimes[0];
        if (ct) {
          info.start_location = info.start_location || ct.mileage_start_location;
          info.mid_location = info.mid_location || ct.mileage_mid_location;
          info.end_location = info.end_location || ct.mileage_end_location;
          if (info.odometer_start == null) info.odometer_start = ct.odometer_start ?? null;
          if (info.odometer_end == null) info.odometer_end = ct.odometer_end ?? null;
        }
      }
      const oStart = info.odometer_start != null ? parseFloat(info.odometer_start) : null;
      const oEnd = info.odometer_end != null ? parseFloat(info.odometer_end) : null;
      info.total_miles = oStart != null && oEnd != null && oEnd > oStart ? (oEnd - oStart).toFixed(1) : null;
      return info;
    },

    isDSPDOneOnOneShift() {
      if (!this.shift) return false;
      let payer = this.shift.payer || this.shift.originalData?.payer;
      if (!payer) {
        const clients = this.shift.clients || this.shift.originalData?.clients || [];
        if (clients.length > 0 && clients[0].payer_type) {
          payer = clients[0].payer_type;
        }
      }
      return payer === "DSPD" && !this.isGroupShift;
    },

    // True when this shift has admin-configured shift-note questions
    // (shift_note_questions table). When present, those questions replace the
    // freeform "Shift Notes" box — the answers are shown read-only above.
    hasShiftNoteQuestions() {
      return this.serviceCodeQuestions.length > 0;
    },

    dspdOneOnOneQuestionAnswers() {
      if (!this.isDSPDOneOnOneShift) return [];
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;
      if (!clientId) return [];
      const allAnswers = this.fullShiftData?.shiftNoteAnswers || this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
      // Include answers matching the client or without a client_id (1-on-1 shifts may not store client_id)
      const clientAnswers = allAnswers.filter(a => !a.client_id || Number(a.client_id) === Number(clientId));

      // Build a map of question_id -> answer from saved answers
      const answerMap = {};
      for (const a of clientAnswers) {
        answerMap[Number(a.question_id)] = {
          answer: a.answer,
          questionText: a.question?.question_text,
        };
      }

      // Show ALL service code questions, with answers where they exist
      if (this.serviceCodeQuestions.length > 0) {
        const results = this.serviceCodeQuestions.map(q => {
          const saved = answerMap[Number(q.id)];
          return {
            questionId: q.id,
            questionText: q.question_text,
            answer: saved ? saved.answer : null,
          };
        });
        // Also include any answers for questions not in the current service code list
        for (const a of clientAnswers) {
          if (!this.serviceCodeQuestions.some(q => Number(q.id) === Number(a.question_id))) {
            results.push({
              questionId: a.question_id,
              questionText: a.question?.question_text || `Question #${a.question_id}`,
              answer: a.answer,
            });
          }
        }
        return results;
      }

      // Fallback: if no service code questions loaded, just show answers
      return clientAnswers.map(a => {
        const questionText = a.question?.question_text || `Question #${a.question_id}`;
        return { questionId: a.question_id, questionText, answer: a.answer };
      });
    },

    // Admin-configured service-code question answers for VR / other non-DSPD,
    // non-group 1-on-1 shifts. Shows every service-code question with its saved
    // answer (or "(no answer)"), plus any answers for questions no longer on
    // the code. Parity with the clock-out modal and the DSPD display.
    vrQuestionAnswers() {
      if (this.isGroupShift || this.isDSPDOneOnOneShift) return [];
      const clients =
        this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;
      const allAnswers =
        this.fullShiftData?.shiftNoteAnswers ||
        this.shift?.shiftNoteAnswers ||
        this.shift?.originalData?.shiftNoteAnswers ||
        [];
      // 1-on-1 shifts may not store client_id on answers; include those too.
      const clientAnswers = allAnswers.filter(
        (a) => !a.client_id || !clientId || Number(a.client_id) === Number(clientId),
      );

      const answerMap = {};
      for (const a of clientAnswers) {
        answerMap[Number(a.question_id)] = {
          answer: a.answer,
          questionText: a.question?.question_text,
        };
      }

      if (this.serviceCodeQuestions.length > 0) {
        const results = this.serviceCodeQuestions.map((q) => {
          const saved = answerMap[Number(q.id)];
          return {
            questionId: q.id,
            questionText: q.question_text,
            answer: saved ? saved.answer : null,
          };
        });
        for (const a of clientAnswers) {
          if (!this.serviceCodeQuestions.some((q) => Number(q.id) === Number(a.question_id))) {
            results.push({
              questionId: a.question_id,
              questionText: a.question?.question_text || `Question #${a.question_id}`,
              answer: a.answer,
            });
          }
        }
        return results;
      }

      return clientAnswers.map((a) => ({
        questionId: a.question_id,
        questionText: a.question?.question_text || `Question #${a.question_id}`,
        answer: a.answer,
      }));
    },

    dspdOneOnOneGoalAnswers() {
      if (!this.isDSPDOneOnOneShift) return [];
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;
      if (!clientId) return [];
      const clientGoalList = this.clientGoals[clientId] || [];
      const allGoalAnswers = this.fullShiftData?.goalAnswers || this.shift?.goalAnswers || this.shift?.originalData?.goalAnswers || [];

      // Build a map of goal_id -> answer from saved answers
      const answerMap = {};
      for (const a of allGoalAnswers) {
        answerMap[Number(a.id_goals)] = {
          answer: a.answer,
          goalText: a.goal?.question_text,
        };
      }

      // Show ALL client goals, with answers where they exist
      if (clientGoalList.length > 0) {
        const results = clientGoalList.map(g => {
          const saved = answerMap[Number(g.id)];
          return {
            goalId: g.id,
            goalText: g.question_text,
            answer: saved ? saved.answer : null,
          };
        });
        // Also include any answers for goals not in the current goal list
        for (const a of allGoalAnswers) {
          if (!clientGoalList.some(g => Number(g.id) === Number(a.id_goals))) {
            results.push({
              goalId: a.id_goals,
              goalText: a.goal?.question_text || `Goal #${a.id_goals}`,
              answer: a.answer,
            });
          }
        }
        return results;
      }

      // Fallback: if no goals loaded, just show answers
      return allGoalAnswers.map(a => {
        const goalText = a.goal?.question_text || `Goal #${a.id_goals}`;
        return { goalId: a.id_goals, goalText, answer: a.answer };
      });
    },

    dspdOneOnOneMedicationLogs() {
      if (!this.isDSPDOneOnOneShift) return [];
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;
      if (!clientId) return [];
      return this.medicationLogs[clientId] || [];
    },

    // Saved answers whose question is no longer on the shift's service codes —
    // they can't be edited (no type/options), so they stay read-only.
    staleQuestionAnswerRows() {
      const rows = this.isDSPDOneOnOneShift
        ? this.dspdOneOnOneQuestionAnswers
        : this.vrQuestionAnswers;
      return rows.filter(
        (qa) =>
          qa.answer != null &&
          qa.answer !== "" &&
          !this.serviceCodeQuestions.some(
            (q) => Number(q.id) === Number(qa.questionId),
          ),
      );
    },

    // The first client's goals, editable during approval (DSPD 1-on-1).
    editableClientGoals() {
      if (!this.isDSPDOneOnOneShift) return [];
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;
      if (!clientId) return [];
      return this.clientGoals[clientId] || [];
    },

    // Saved goal answers whose goal is no longer on the client (read-only).
    staleGoalAnswerRows() {
      return this.dspdOneOnOneGoalAnswers.filter(
        (ga) =>
          ga.answer != null &&
          ga.answer !== "" &&
          !this.editableClientGoals.some(
            (g) => Number(g.id) === Number(ga.goalId),
          ),
      );
    },

    groupCaregivers() {
      if (!this.shift) return [];
      return this.shift.caregivers || this.shift.originalData?.caregivers || [];
    },

    groupClients() {
      if (!this.shift) return [];
      return this.shift.clients || this.shift.originalData?.clients || [];
    },

    groupCaregiverClockTimes() {
      if (!this.shift) return [];
      return this.shift.caregiverClockTimes || this.shift.originalData?.caregiverClockTimes || [];
    },

    // Captured clock-in/out coordinates for 1-on-1 shifts ("lat,lng" strings).
    clockInGeocode() {
      const s = this.shift || {};
      return s.clock_in_geocode || s.originalData?.clock_in_geocode || null;
    },

    clockOutGeocode() {
      const s = this.shift || {};
      return s.clock_out_geocode || s.originalData?.clock_out_geocode || null;
    },

    groupClientDetails() {
      if (!this.shift) return [];
      return this.shift.clientDetails || this.shift.originalData?.clientDetails || [];
    },

    shiftServiceCode() {
      const codes = this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      if (codes.length > 0) return codes[0].code;
      return null;
    },

    isLSService() {
      if (!this.shift) return false;

      // Check direct service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "LS");
      }

      return false;
    },

    isSEDService() {
      if (!this.shift) return false;

      // Check direct service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SED");
      }

      return false;
    },

    parsedLSActivities() {
      if (!this.shift || !this.shift.ls_activities) return [];

      // If it's already an array, return it
      if (Array.isArray(this.shift.ls_activities)) {
        return this.shift.ls_activities;
      }

      // If it's a string, try to parse it as JSON
      if (typeof this.shift.ls_activities === "string") {
        try {
          const parsed = JSON.parse(this.shift.ls_activities);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON parsing fails, split by comma or return as single item
          return this.shift.ls_activities.includes(",")
            ? this.shift.ls_activities.split(",").map((item) => item.trim())
            : [this.shift.ls_activities];
        }
      }

      return [];
    },

    parsedSEDActivities() {
      if (!this.shift || !this.shift.sed_activities) return [];

      // If it's already an array, return it
      if (Array.isArray(this.shift.sed_activities)) {
        return this.shift.sed_activities;
      }

      // If it's a string, try to parse it as JSON
      if (typeof this.shift.sed_activities === "string") {
        try {
          const parsed = JSON.parse(this.shift.sed_activities);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON parsing fails, split by comma or return as single item
          return this.shift.sed_activities.includes(",")
            ? this.shift.sed_activities.split(",").map((item) => item.trim())
            : [this.shift.sed_activities];
        }
      }

      return [];
    },

    isSBService() {
      if (!this.shift) return false;

      // Check direct service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "SB");
      }

      return false;
    },

    parsedSBActivities() {
      if (!this.shift || !this.shift.sb_activities) return [];

      // If it's already an array, return it
      if (Array.isArray(this.shift.sb_activities)) {
        return this.shift.sb_activities;
      }

      // If it's a string, try to parse it as JSON
      if (typeof this.shift.sb_activities === "string") {
        try {
          const parsed = JSON.parse(this.shift.sb_activities);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON parsing fails, split by comma or return as single item
          return this.shift.sb_activities.includes(",")
            ? this.shift.sb_activities.split(",").map((item) => item.trim())
            : [this.shift.sb_activities];
        }
      }

      return [];
    },

    // Check if the service is Job Development (CE, SE, SJBT, TWE are all JD variations)
    // Legacy JD documentation already stored on the shift's jd_* columns
    // (from before the JD fields became configurable shift_note_questions)
    hasLegacyJdData() {
      const s = this.shift || {};
      const o = s.originalData || {};
      return !!(
        s.jd_activity || s.jd_outcome || s.jd_next_steps ||
        o.jd_activity || o.jd_outcome || o.jd_next_steps
      );
    },

    // Show the bespoke jd_* fields only for shifts already documented that
    // way, or as a fallback when the JD code has no configured questions.
    showJdFields() {
      return (
        this.isJDService &&
        (this.hasLegacyJdData || this.serviceCodeQuestions.length === 0)
      );
    },

    // Legacy DSPD 1-on-1 concerns already stored as shifts.shift_notes
    hasLegacyConcerns() {
      const s = this.shift || {};
      const o = s.originalData || {};
      const full = this.fullShiftData || {};
      return !!(
        (full.shift_notes || s.shift_notes || o.shift_notes || "")
          .toString()
          .trim()
      );
    },

    // Hardcoded concerns textarea: legacy shifts and question-less codes only
    showConcernsField() {
      return (
        this.isDSPDOneOnOneShift &&
        (this.hasLegacyConcerns || this.serviceCodeQuestions.length === 0)
      );
    },

    // Question list actually rendered/validated — hides the seeded concerns
    // question while the legacy concerns textarea is active. Equals
    // serviceCodeQuestions verbatim outside that mode (VR shifts included).
    visibleDspdQuestions() {
      if (!this.showConcernsField) return this.serviceCodeQuestions;
      return this.serviceCodeQuestions.filter(
        (q) => q.question_key !== "dspd_concerns",
      );
    },

    isJDService() {
      if (!this.shift) return false;

      // Check direct service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        // Check if any service code is a Job Development variant
        return this.shift.service_codes.some(
          (sc) =>
            sc.code === "CE" ||
            sc.code === "SE" ||
            sc.code === "SJBT" ||
            sc.code === "TWE",
        );
      }

      return false;
    },

    isJCService() {
      if (!this.shift) return false;

      // Check direct service_codes array
      if (this.shift.service_codes && this.shift.service_codes.length > 0) {
        return this.shift.service_codes.some((sc) => sc.code === "JC");
      }

      return false;
    },

    parsedJCPrimaryActivities() {
      if (!this.shift || !this.shift.jc_primary_activities) return [];

      // If it's already an array, return it
      if (Array.isArray(this.shift.jc_primary_activities)) {
        return this.shift.jc_primary_activities;
      }

      // If it's a string, try to parse it as JSON
      if (typeof this.shift.jc_primary_activities === "string") {
        try {
          const parsed = JSON.parse(this.shift.jc_primary_activities);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON parsing fails, split by comma or return as single item
          return this.shift.jc_primary_activities.includes(",")
            ? this.shift.jc_primary_activities
                .split(",")
                .map((item) => item.trim())
            : [this.shift.jc_primary_activities];
        }
      }

      return [];
    },

    parsedJCSecondaryActivities() {
      if (!this.shift || !this.shift.jc_secondary_activities) return [];

      // If it's already an array, return it
      if (Array.isArray(this.shift.jc_secondary_activities)) {
        return this.shift.jc_secondary_activities;
      }

      // If it's a string, try to parse it as JSON
      if (typeof this.shift.jc_secondary_activities === "string") {
        try {
          const parsed = JSON.parse(this.shift.jc_secondary_activities);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // If JSON parsing fails, split by comma or return as single item
          return this.shift.jc_secondary_activities.includes(",")
            ? this.shift.jc_secondary_activities
                .split(",")
                .map((item) => item.trim())
            : [this.shift.jc_secondary_activities];
        }
      }

      return [];
    },
  },

  watch: {
    async visible(newVal) {
      if (newVal) {
        this.resetForm();
        this.initializeEditableNotes();
        // Load generic VR activity options, then map saved selections to ids.
        await this.loadVrActivities();
        this.loadSelectedVrActivities(this.shift);
        this.initializeJDFields();
        if (this.isJCService) {
          await this.fetchJobCoachingActivities();
          this.initializeJCActivities();
        }
        if (this.isGroupShift) {
          this.fetchGroupShiftQuestionData();
          this.fetchMedicationLogs();
        }
        if (this.isDSPDOneOnOneShift) {
          await this.fetchDSPDOneOnOneQuestionData();
          // Re-initialize notes after full shift data is loaded (concerns stored as shift_notes)
          if (this.fullShiftData) {
            this.editableShiftNotes = this.fullShiftData.shift_notes
              || this.editableShiftNotes;
          }
          this.seedEditableAnswers();
          this.fetchMedicationLogs();
        }
        // VR / other 1-on-1 shifts: load admin-configured service-code
        // questions so their answers can be reviewed and edited when
        // approving (parity with the clock-out modal). Group and DSPD
        // 1-on-1 are handled above.
        if (!this.isGroupShift && !this.isDSPDOneOnOneShift) {
          await this.fetchVRQuestionData();
          this.seedEditableAnswers();
        }
        this.checkAuthRemaining();
        this.resolveShiftClockLocations();
      }
    },
  },

  methods: {
    // multi_select answers are stored as a comma-joined string so every
    // read/display/report path renders them as-is; convert to/from the array
    // the multiple-select control needs.
    parseMultiAnswer(value) {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === "") return [];
      return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    joinMultiAnswer(value) {
      return (Array.isArray(value) ? value : []).join(", ");
    },

    // Seed the editable question/goal answer maps from the saved answers so
    // the approver starts from what the caregiver documented.
    seedEditableAnswers() {
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      const clientId = clients.length > 0 ? clients[0].id : null;

      const allAnswers =
        this.fullShiftData?.shiftNoteAnswers ||
        this.shift?.shiftNoteAnswers ||
        this.shift?.originalData?.shiftNoteAnswers ||
        [];
      // 1-on-1 shifts may not store client_id on answers; include those too.
      const clientAnswers = allAnswers.filter(
        (a) => !a.client_id || !clientId || Number(a.client_id) === Number(clientId),
      );
      for (const a of clientAnswers) {
        let ans = a.answer || "";
        if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
          ans = ans.toLowerCase();
        }
        this.$set(this.editableQuestionAnswers, a.question_id, ans);
      }

      const allGoalAnswers =
        this.fullShiftData?.goalAnswers ||
        this.shift?.goalAnswers ||
        this.shift?.originalData?.goalAnswers ||
        [];
      for (const a of allGoalAnswers) {
        let ans = a.answer || "";
        if (ans.toLowerCase() === "yes" || ans.toLowerCase() === "no") {
          ans = ans.toLowerCase();
        }
        this.$set(this.editableGoalAnswers, a.id_goals, ans);
      }
    },

    resetForm() {
      this.approvalNotes = "";
      this.editableShiftNotes = "";
      this.selectedLSActivities = [];
      this.selectedSEDActivities = [];
      this.selectedSBActivities = [];
      this.editableJDActivity = "";
      this.editableJDOutcome = "";
      this.editableJDNextSteps = "";
      this.selectedJCPrimaryActivities = [];
      this.selectedJCSecondaryActivities = [];
      this.serviceCodeQuestions = [];
      this.clientGoals = {};
      this.editableQuestionAnswers = {};
      this.editableGoalAnswers = {};
      this.fullShiftData = null;
      this.medicationLogs = {};
      this.authWarning = false;
      this.authWarningMessage = "";
      this.authAcknowledged = false;
      this.initializeMileageApproval();
    },

    // Build the mileage-approval rows: the shift-level trip plus any
    // per-caregiver clock-row trips (group/side actors). Approval defaults
    // ON with the recorded miles prefilled; editing the number = adjustment.
    initializeMileageApproval() {
      const s = this.shift || {};
      const o = s.originalData || {};
      const rows = [];
      const trip = (start, end) => {
        const a = parseFloat(start);
        const b = parseFloat(end);
        if (isNaN(a) || isNaN(b) || b <= a) return null;
        return Math.round((b - a) * 10) / 10;
      };

      const shiftTrip = trip(s.odometer_start ?? o.odometer_start, s.odometer_end ?? o.odometer_end);
      if (shiftTrip !== null) {
        rows.push({ key: "shift", id_users: null, label: "", recorded: shiftTrip, approved: true, miles: shiftTrip });
      }

      const clockTimes = s.caregiverClockTimes || o.caregiverClockTimes || [];
      for (const ct of Array.isArray(clockTimes) ? clockTimes : []) {
        const ctTrip = trip(ct.odometer_start, ct.odometer_end);
        if (ctTrip === null) continue;
        const name = ct.caregiver
          ? `${ct.caregiver.first_name} ${ct.caregiver.last_name}`
          : `Caregiver #${ct.id_users}`;
        rows.push({ key: `ct-${ct.id_users}`, id_users: ct.id_users, label: name, recorded: ctTrip, approved: true, miles: ctTrip });
      }
      this.mileageTripRows = rows;
      this.mileageAllowances = {};
      if (rows.length > 0) this.fetchMileageAllowances();
    },

    // The caregiver a trip row belongs to: clock rows carry id_users; the
    // shift-level trip belongs to the primary caregiver.
    mileageRowCaregiverId(row) {
      if (row.id_users !== null) return row.id_users;
      const caregivers =
        this.shift?.caregivers || this.shift?.originalData?.caregivers || [];
      const primary =
        caregivers.find(
          (c) => (c.link_shifts_caregiver_users?.role || c.role || "primary") === "primary",
        ) || caregivers[0];
      return primary ? primary.id : null;
    },

    async fetchMileageAllowances() {
      const date =
        this.shift?.originalData?.clock_in ||
        this.shift?.originalData?.clockIn ||
        this.shift?.date ||
        new Date().toISOString();
      const ids = [
        ...new Set(
          this.mileageTripRows
            .map((r) => this.mileageRowCaregiverId(r))
            .filter((id) => id != null),
        ),
      ];
      await Promise.all(
        ids.map(async (id) => {
          try {
            const allowance = await shiftsService.getMileageAllowance({
              caregiverId: id,
              date,
            });
            this.$set(this.mileageAllowances, id, allowance);
          } catch {
            /* meter simply doesn't render */
          }
        }),
      );
    },

    allowanceFor(row) {
      const id = this.mileageRowCaregiverId(row);
      return id != null ? this.mileageAllowances[id] : null;
    },

    // What approving this row adds, in dollars (miles x rate; approximate
    // for non-transport trips since the daily deduction depends on the
    // whole day's driving — hence the ≈ in the display).
    allowanceAdds(row) {
      const a = this.allowanceFor(row);
      if (!a || !row.approved) return 0;
      const miles = parseFloat(row.miles);
      if (isNaN(miles) || miles <= 0) return 0;
      return Math.round(miles * a.rate * 100) / 100;
    },

    // Pending dollars EXCLUDING this trip (the trip is itself pending until
    // approved, so showing it in both "pending" and "adds" would double it).
    allowancePendingOther(row) {
      const a = this.allowanceFor(row);
      if (!a) return 0;
      const thisTrip = Math.round((row.recorded || 0) * a.rate * 100) / 100;
      return Math.max(0, Math.round((a.pendingDollars - thisTrip) * 100) / 100);
    },

    allowanceOverBy(row) {
      const a = this.allowanceFor(row);
      if (!a || !row.approved) return 0;
      const projected = a.approvedDollars + this.allowanceAdds(row);
      return projected > a.capDollars
        ? Math.round((projected - a.capDollars) * 100) / 100
        : 0;
    },

    allowanceClass(row) {
      const a = this.allowanceFor(row);
      if (!a) return "";
      if (this.allowanceOverBy(row) > 0) return "allowance-over";
      const projected = a.approvedDollars + this.allowanceAdds(row);
      if (projected >= (a.capDollars * a.thresholdPercent) / 100) return "allowance-warn";
      return "";
    },

    allowancePeriodLabel(row) {
      const a = this.allowanceFor(row);
      if (!a) return "";
      const md = (s) => {
        const [, m, d] = (s || "").split("-");
        return m && d ? `${parseInt(m, 10)}/${parseInt(d, 10)}` : "";
      };
      return `${md(a.periodStart)}–${md(a.periodEnd)}`;
    },

    async checkAuthRemaining() {
      this.authWarning = false;
      this.authWarningMessage = "";
      this.authAcknowledged = false;

      if (!this.shift) return;
      const payer = this.shift.payer || this.shift.originalData?.payer;
      if (payer === "Non Service") return;

      const clientId =
        this.shift.originalData?.clients?.[0]?.id ||
        this.shift.clients?.[0]?.id ||
        null;
      if (!clientId) return;

      // Get service code from shift
      const serviceCode = this.shift.service_codes?.[0] ||
        this.shift.originalData?.service_codes?.[0] || null;
      if (!serviceCode) return;

      // Calculate hours that will be billed
      const clockInTime =
        this.shift.originalData?.clockIn ||
        this.shift.originalData?.clock_in ||
        this.shift.actualStartTime;
      const clockOutTime =
        this.shift.originalData?.clockOut ||
        this.shift.originalData?.clock_out ||
        this.shift.actualEndTime;

      let hoursWorked = 0;
      if (clockInTime && clockOutTime) {
        const clockIn = new Date(clockInTime);
        const clockOut = new Date(clockOutTime);
        if (!isNaN(clockIn.getTime()) && !isNaN(clockOut.getTime())) {
          let diffMs = clockOut.getTime() - clockIn.getTime();
          if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
          const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
          let totalBreakMs = 0;
          for (const b of breaks.filter(b => b.break_start && b.break_end)) {
            totalBreakMs += new Date(b.break_end).getTime() - new Date(b.break_start).getTime();
          }
          hoursWorked = (diffMs - totalBreakMs) / (1000 * 60 * 60);
        }
      }

      if (hoursWorked <= 0) return;

      try {
        const auths = await authorizationsService.getByClientId(clientId);
        if (!auths || auths.length === 0) return;

        const matchingAuths = auths.filter(a => a.id_service_codes === serviceCode.id && a.status === "active");
        if (matchingAuths.length === 0) return;

        const isDSPD = payer === "DSPD" || payer === "Private";
        if (isDSPD) {
          const totalUnitsRemaining = matchingAuths.reduce((sum, a) => {
            return sum + Math.max(0, (parseFloat(a.units) || 0) - (parseFloat(a.units_used) || 0));
          }, 0);
          const unitsNeeded = this.shift.originalData?.clients?.length || 1;
          if (unitsNeeded > totalUnitsRemaining) {
            this.authWarning = true;
            this.authWarningMessage = `Warning: Only ${totalUnitsRemaining} units remaining on the authorization for ${serviceCode.code}, but this shift requires ${unitsNeeded} unit(s). Approving will exceed the remaining units.`;
          }
        } else {
          const totalHoursRemaining = matchingAuths.reduce((sum, a) => sum + (a.hours_remaining || 0), 0);
          if (hoursWorked > totalHoursRemaining) {
            this.authWarning = true;
            this.authWarningMessage = `Warning: Only ${totalHoursRemaining.toFixed(1)} hours remaining on the authorization for ${serviceCode.code}, but this shift is ${hoursWorked.toFixed(1)} hours. Approving will exceed the remaining hours.`;
          }
        }
      } catch (error) {
      }
    },

    initializeEditableNotes() {
      // Initialize editable notes from the shift data
      if (this.shift) {
        // Check all possible note sources (including jc_shift_notes for backward compatibility)
        this.editableShiftNotes =
          this.shift.shift_notes ||
          this.shift.jc_shift_notes ||
          this.shift.notes ||
          "";
      }
    },

    // vrActivities mixin hook: a shift may carry multiple VR codes.
    getShiftVrCodes() {
      return (this.shift?.service_codes || []).map((sc) => sc.code);
    },

    initializeJDFields() {
      if (!this.shift) return;

      this.editableJDActivity = this.shift.jd_activity || "";
      this.editableJDOutcome = this.shift.jd_outcome || "";
      this.editableJDNextSteps = this.shift.jd_next_steps || "";
    },

    async fetchJobCoachingActivities() {
      if (this.jobCoachingActivities.length > 0) {
        // Already loaded
        return;
      }

      try {
        this.loadingJobCoachingActivities = true;
        const response = await jobCoachingActivitiesService.list();
        this.jobCoachingActivities = response || [];
      } catch (error) {
        this.$notifyError("Failed to load job coaching activities");
      } finally {
        this.loadingJobCoachingActivities = false;
      }
    },

    initializeJCActivities() {
      if (!this.shift) return;

      // Parse primary activities
      const primaryActivities = this.parsedJCPrimaryActivities;
      if (primaryActivities.length > 0) {
        this.selectedJCPrimaryActivities = primaryActivities
          .map((activityName) => {
            const activity = this.jobCoachingActivities.find(
              (act) => act.name === activityName,
            );
            return activity ? activity.id : null;
          })
          .filter(Boolean);
      }

      // Parse secondary activities
      const secondaryActivities = this.parsedJCSecondaryActivities;
      if (secondaryActivities.length > 0) {
        this.selectedJCSecondaryActivities = secondaryActivities
          .map((activityName) => {
            const activity = this.jobCoachingActivities.find(
              (act) => act.name === activityName,
            );
            return activity ? activity.id : null;
          })
          .filter(Boolean);
      }
    },

    async fetchGroupShiftQuestionData() {
      // Fetch the full shift data (includes shiftNoteAnswers and goalAnswers)
      const shiftId = this.shift?.originalData?.id || this.shift?.id;
      if (shiftId) {
        try {
          this.fullShiftData = await shiftsService.getShiftTasks(shiftId);
        } catch {
          this.fullShiftData = null;
        }
      }

      // Fetch service code questions for all service codes on the shift
      const serviceCodes = this.fullShiftData?.service_codes || this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      const allQuestions = [];
      const seenIds = new Set();
      for (const sc of serviceCodes) {
        try {
          const questions = await shiftNoteQuestionsService.getByServiceCode(sc.code);
          for (const q of (questions || [])) {
            if (!seenIds.has(q.id)) {
              seenIds.add(q.id);
              allQuestions.push(q);
            }
          }
        } catch {
          // ignore
        }
      }
      this.serviceCodeQuestions = allQuestions.sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );

      // Fetch client goals for each client in the group
      const clientDetails = this.groupClientDetails;
      for (const cd of clientDetails) {
        const clientId = cd.id_clients || cd.client?.id;
        if (clientId && !this.clientGoals[clientId]) {
          try {
            const response = await clientsService.getGoals(clientId);
            this.$set(this.clientGoals, clientId, response.goals || []);
          } catch {
            this.$set(this.clientGoals, clientId, []);
          }
        }
      }
    },

    async fetchDSPDOneOnOneQuestionData() {
      // Fetch the full shift data (includes shiftNoteAnswers and goalAnswers)
      const shiftId = this.shift?.originalData?.id || this.shift?.id;
      if (shiftId) {
        try {
          this.fullShiftData = await shiftsService.getShiftTasks(shiftId);
        } catch {
          this.fullShiftData = null;
        }
      }

      // Fetch service code questions for this shift's service codes
      const serviceCodes = this.fullShiftData?.service_codes || this.shift?.service_codes || this.shift?.originalData?.service_codes || [];
      const allQuestions = [];
      const seenIds = new Set();
      for (const sc of serviceCodes) {
        try {
          const questions = await shiftNoteQuestionsService.getByServiceCode(sc.code);
          for (const q of (questions || [])) {
            if (!seenIds.has(q.id)) {
              seenIds.add(q.id);
              allQuestions.push(q);
            }
          }
        } catch {
          // ignore
        }
      }
      this.serviceCodeQuestions = allQuestions.sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );

      // Fetch client goals
      const clients = this.shift?.clients || this.shift?.originalData?.clients || [];
      if (clients.length > 0) {
        const clientId = clients[0].id;
        if (clientId) {
          try {
            const response = await clientsService.getGoals(clientId);
            this.$set(this.clientGoals, clientId, response.goals || []);
          } catch {
            this.$set(this.clientGoals, clientId, []);
          }
        }
      }
    },

    async fetchVRQuestionData() {
      // Load full shift data (for shiftNoteAnswers) and the admin-configured
      // service-code questions, so saved answers display when approving VR /
      // other non-DSPD, non-group 1-on-1 shifts. Mirrors ClockOutModal's
      // fetchVRShiftNoteData so the approver sees the same custom notes.
      const shiftId = this.shift?.originalData?.id || this.shift?.id;
      if (shiftId && !this.fullShiftData) {
        try {
          this.fullShiftData = await shiftsService.getShiftTasks(shiftId);
        } catch {
          this.fullShiftData = null;
        }
      }

      const serviceCodes =
        this.fullShiftData?.service_codes ||
        this.shift?.service_codes ||
        this.shift?.originalData?.service_codes ||
        [];
      const allQuestions = [];
      const seenIds = new Set();
      for (const sc of serviceCodes) {
        try {
          const questions = await shiftNoteQuestionsService.getByServiceCode(sc.code);
          for (const q of questions || []) {
            if (!seenIds.has(q.id)) {
              seenIds.add(q.id);
              allQuestions.push(q);
            }
          }
        } catch {
          // ignore
        }
      }
      this.serviceCodeQuestions = allQuestions.sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
      );
    },

    getClientQuestionAnswers(clientDetail) {
      const clientId = clientDetail.id_clients || clientDetail.client?.id;
      if (!clientId) return [];

      // Get answers from the full shift data or shift-level shiftNoteAnswers array,
      // scoped to this specific client.
      const allAnswers = this.fullShiftData?.shiftNoteAnswers || this.shift?.shiftNoteAnswers || this.shift?.originalData?.shiftNoteAnswers || [];
      const clientAnswers = allAnswers.filter(a => Number(a.client_id) === Number(clientId));

      // Build a map of question_id -> answer for THIS client only
      const answerMap = {};
      for (const a of clientAnswers) {
        answerMap[Number(a.question_id)] = {
          answer: a.answer,
          questionText: a.question?.question_text,
        };
      }
      // Legacy data (already tied to this clientDetail)
      const legacyAnswers = clientDetail.question_answers || clientDetail.shift_note_answers || [];
      for (const a of legacyAnswers) {
        const qId = Number(a.question_id || a.questionId);
        if (!answerMap[qId]) {
          answerMap[qId] = { answer: a.answer };
        }
      }

      // Only include questions this client actually answered. A question with no
      // saved answer for this client belongs to another client's service code and
      // would otherwise render as "(no answer)" noise. Dedup by question text so
      // identical questions that exist as separate rows across service codes
      // don't appear twice.
      const results = [];
      const seenTexts = new Set();
      for (const [qId, saved] of Object.entries(answerMap)) {
        if (saved.answer == null || saved.answer === '') continue;
        const q = this.serviceCodeQuestions.find(sq => Number(sq.id) === Number(qId));
        const text = (q && q.question_text) || saved.questionText || `Question #${qId}`;
        if (seenTexts.has(text)) continue;
        seenTexts.add(text);
        results.push({
          questionId: Number(qId),
          questionText: text,
          answer: saved.answer,
        });
      }
      return results;
    },

    getClientGoalAnswers(clientDetail) {
      const clientId = clientDetail.id_clients || clientDetail.client?.id;
      if (!clientId) return [];

      // Goals are owned by a specific client; only this client's goals belong here.
      const clientGoalList = this.clientGoals[clientId] || [];
      if (clientGoalList.length === 0) return [];

      // Build an answer map from the shift-level goal answers.
      const allGoalAnswers = this.fullShiftData?.goalAnswers || this.shift?.goalAnswers || this.shift?.originalData?.goalAnswers || [];
      const answerMap = {};
      for (const a of allGoalAnswers) {
        answerMap[Number(a.id_goals)] = { answer: a.answer };
      }
      const legacyAnswers = clientDetail.goal_answers || [];
      for (const a of legacyAnswers) {
        const gId = Number(a.goal_id || a.goalId);
        if (!answerMap[gId]) answerMap[gId] = { answer: a.answer };
      }

      // Show only goals owned by this client. Other clients' goal answers
      // would otherwise leak into this section.
      return clientGoalList.map(g => ({
        goalId: g.id,
        goalText: g.question_text,
        answer: answerMap[Number(g.id)] ? answerMap[Number(g.id)].answer : null,
      }));
    },

    handleCancel() {
      this.isVisible = false;
      this.resetForm();
    },

    async handleApprove() {
      if (!this.shift || !this.shift.id) {
        this.$notifyError("Shift data is not available. Please try again.");
        return;
      }

      if (this.authWarning && !this.authAcknowledged) {
        this.$notifyError("Please acknowledge the authorization hours/units warning before approving.");
        return;
      }

      // Documentation requirements — mirrors ClockOutModal / ApproveMissedShift
      // so a shift can't be approved without its notes. Group shifts document
      // per client in shift_client_details and are skipped, like everywhere else.

      // Required shift-note questions (any 1-on-1 shift; skipped when the
      // legacy JD fields carry the documentation instead, and minus the
      // seeded concerns question while the legacy concerns textarea is shown)
      if (!this.isGroupShift && !this.showJdFields) {
        for (const q of this.visibleDspdQuestions) {
          if (
            q.required &&
            !(this.editableQuestionAnswers[q.id] || "").toString().trim()
          ) {
            this.$notifyError(`Please answer: "${q.question_text}"`);
            return;
          }
        }
      }

      // Notes for non-JD, non-DSPD-1-on-1 services (skipped when shift-note
      // questions apply — they replace the freeform notes box)
      if (
        !this.isJDService &&
        !this.isGroupShift &&
        !this.isDSPDOneOnOneShift &&
        !this.hasShiftNoteQuestions
      ) {
        if (!(this.editableShiftNotes || "").trim()) {
          this.$notifyError("Shift Notes are required");
          return;
        }
        if (this.editableShiftNotes.length < 20) {
          this.$notifyError("Shift Notes must be at least 20 characters");
          return;
        }
      }

      // Validate legacy Job Development fields (only when they are shown)
      if (this.showJdFields && !this.isGroupShift) {
        if (!(this.editableJDActivity || "").trim()) {
          this.$notifyError("Job Development Activity is required");
          return;
        }
        if (this.editableJDActivity.length < 20) {
          this.$notifyError(
            "Job Development Activity must be at least 20 characters",
          );
          return;
        }
        if (!(this.editableJDOutcome || "").trim()) {
          this.$notifyError("Outcome of Activity is required");
          return;
        }
        if (this.editableJDOutcome.length < 20) {
          this.$notifyError(
            "Outcome of Activity must be at least 20 characters",
          );
          return;
        }
        if (!(this.editableJDNextSteps || "").trim()) {
          this.$notifyError("Next Steps Forward is required");
          return;
        }
        if (this.editableJDNextSteps.length < 20) {
          this.$notifyError(
            "Next Steps Forward must be at least 20 characters",
          );
          return;
        }
      }

      // Validate JC specific requirements
      if (this.isJCService && !this.isGroupShift) {
        if (this.selectedJCPrimaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one primary job coaching activity",
          );
          return;
        }
        if (this.selectedJCSecondaryActivities.length === 0) {
          this.$notifyError(
            "Please select at least one secondary job coaching activity",
          );
          return;
        }
        if (this.selectedJCPrimaryActivities.length > 3) {
          this.$notifyError("Please select no more than 3 primary activities");
          return;
        }
        if (this.selectedJCSecondaryActivities.length > 3) {
          this.$notifyError(
            "Please select no more than 3 secondary activities",
          );
          return;
        }
      }

      // Validate generic VR activities (each VR code with a list requires >=1).
      const vrActivityError = this.validateVrActivities({ requireMin: true });
      if (vrActivityError) {
        this.$notifyError(vrActivityError);
        return;
      }

      this.approving = true;
      try {
        const clockInTime =
          this.shift.originalData?.clockIn ||
          this.shift.originalData?.clock_in ||
          this.shift.actualStartTime;

        const clockOutTime =
          this.shift.originalData?.clockOut ||
          this.shift.originalData?.clock_out ||
          this.shift.actualEndTime;

        let hoursWorked = 0;
        if (clockInTime && clockOutTime) {
          const clockIn = new Date(clockInTime);
          const clockOut = new Date(clockOutTime);

          if (!isNaN(clockIn.getTime()) && !isNaN(clockOut.getTime())) {
            let diffMs = clockOut.getTime() - clockIn.getTime();

            if (diffMs < 0) {
              const nextDayClockOut = new Date(clockOut);
              nextDayClockOut.setDate(nextDayClockOut.getDate() + 1);
              diffMs = nextDayClockOut.getTime() - clockIn.getTime();
            }

            // Calculate total break time and subtract from hours worked
            const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
            const completedBreaks = breaks.filter(b => b.break_start && b.break_end);

            let totalBreakMs = 0;
            for (const breakRecord of completedBreaks) {
              const breakStart = new Date(breakRecord.break_start);
              const breakEnd = new Date(breakRecord.break_end);

              if (!isNaN(breakStart.getTime()) && !isNaN(breakEnd.getTime())) {
                totalBreakMs += breakEnd.getTime() - breakStart.getTime();
              }
            }

            // Subtract break time from total time to get billable hours
            const billableMs = diffMs - totalBreakMs;
            hoursWorked = billableMs / (1000 * 60 * 60);
          }
        }

        const clientId =
          this.shift.originalData?.clients?.[0]?.id ||
          this.shift.clients?.[0]?.id ||
          null;

        const loginStore = useLoginStore();
        const leadId = loginStore.user?.id;

        const payload = {
          shiftId: this.shift.originalData?.id || this.shift.id,
          leadId: leadId,
          hoursWorked: hoursWorked,
          clientId: clientId,
        };

        // Add notes to payload - always include to capture any admin edits
        // For JD services, clear shift_notes since they use JD-specific fields instead
        payload.shift_notes = this.isJDService ? "" : this.editableShiftNotes;
        if (this.isDSPDOneOnOneShift && !this.showConcernsField) {
          // Concerns live in question answers on this shift — leave the
          // legacy notes column untouched
          delete payload.shift_notes;
        }

        // Generic VR activities (LS/SED/SB and any other VR code), keyed by
        // service code. This modal sees all of the shift's codes, so it sends
        // the full object (the backend restricts to codes on the shift).
        payload.vrActivitiesSelected = this.buildVrActivitiesPayload();

        // Add JD fields - send empty strings if not JD service to clear old data
        if (this.showJdFields) {
          payload.jd_activity = this.editableJDActivity;
          payload.jd_outcome = this.editableJDOutcome;
          payload.jd_next_steps = this.editableJDNextSteps;
        } else {
          payload.jd_activity = "";
          payload.jd_outcome = "";
          payload.jd_next_steps = "";
        }

        // Admin-edited shift-note question answers (1-on-1 shifts). Empty
        // answers are not sent — matching the clock-out flow — so existing
        // answers can be revised but not cleared.
        if (!this.isGroupShift && this.serviceCodeQuestions.length) {
          payload.questionAnswers = this.serviceCodeQuestions
            .map((q) => ({
              questionId: Number(q.id),
              answer: (this.editableQuestionAnswers[q.id] || "").toString().trim(),
            }))
            .filter((qa) => qa.answer);
        }

        // Admin-edited client goal answers (DSPD 1-on-1 shifts)
        if (this.isDSPDOneOnOneShift && this.editableClientGoals.length) {
          payload.goalAnswers = this.editableClientGoals
            .map((g) => ({
              goalId: Number(g.id),
              answer: (this.editableGoalAnswers[g.id] || "").toString().trim(),
            }))
            .filter((ga) => ga.answer);
        }

        // Add JC activities - send empty arrays if not JC service to clear old data
        if (this.isJCService) {
          payload.jcPrimaryActivities = this.selectedJCPrimaryActivities
            .map(
              (actId) =>
                this.jobCoachingActivities.find((act) => act.id === actId)
                  ?.name,
            )
            .filter(Boolean);

          payload.jcSecondaryActivities = this.selectedJCSecondaryActivities
            .map(
              (actId) =>
                this.jobCoachingActivities.find((act) => act.id === actId)
                  ?.name,
            )
            .filter(Boolean);
        } else {
          payload.jcPrimaryActivities = [];
          payload.jcSecondaryActivities = [];
        }

        // Mileage approval decisions (per recorded trip). Absent when the
        // shift has no trips — the server then has nothing to flag.
        if (this.mileageTripRows.length > 0) {
          const shiftRow = this.mileageTripRows.find((r) => r.key === "shift");
          payload.mileageApproval = {
            approved: shiftRow ? shiftRow.approved : true,
            miles: shiftRow && shiftRow.approved ? parseFloat(shiftRow.miles) : undefined,
            rows: this.mileageTripRows
              .filter((r) => r.id_users !== null)
              .map((r) => ({
                id_users: r.id_users,
                approved: r.approved,
                miles: r.approved ? parseFloat(r.miles) : undefined,
              })),
          };
        }

        await shiftsService.approveShift(payload);

        // Show different message for Non Service shifts (no authorization hours update)
        const isNonService =
          this.shift.payer === "Non Service" ||
          this.shift.originalData?.payer === "Non Service";
        const successMessage = isNonService
          ? "Shift approved successfully!"
          : "Shift approved successfully! Authorization hours have been updated.";

        this.$notifySuccess(successMessage);
        this.isVisible = false;
        this.resetForm();
        this.$emit("shift-approved", {
          shiftId: this.shift.id,
          approvalNotes: this.approvalNotes.trim(),
          hoursWorked: hoursWorked,
        });
      } catch (error) {
        this.$notifyError(
          "Error approving shift: " +
            (error.response?.data?.message || error.message),
        );
      } finally {
        this.approving = false;
      }
    },

    // Reverse-geocode all clock-in/out coordinates on this shift (1-on-1 plus
    // every per-caregiver clock time on group shifts) so their addresses show.
    resolveShiftClockLocations() {
      const geocodes = [this.clockInGeocode, this.clockOutGeocode];
      for (const ct of this.groupCaregiverClockTimes) {
        geocodes.push(ct.clock_in_geocode, ct.clock_out_geocode);
      }
      this.resolveClockLocations(geocodes);
    },

    // Per-caregiver clock-in/out geocode for group shifts ('in' | 'out').
    getCaregiverGeocode(caregiverId, which) {
      const clockTimes = this.groupCaregiverClockTimes;
      if (!clockTimes.length) return null;
      const ct = clockTimes.find(
        (c) => Number(c.id_users) === Number(caregiverId),
      );
      if (!ct) return null;
      return (which === "out" ? ct.clock_out_geocode : ct.clock_in_geocode) || null;
    },

    getCaregiverClockIn(caregiverId) {
      const clockTimes = this.groupCaregiverClockTimes;
      if (!clockTimes.length) return null;
      const ct = clockTimes.find(
        (c) => Number(c.id_users) === Number(caregiverId),
      );
      return ct?.clock_in ? this.formatClockTime(ct.clock_in) : null;
    },

    getCaregiverClockOut(caregiverId) {
      const clockTimes = this.groupCaregiverClockTimes;
      if (!clockTimes.length) return null;
      const ct = clockTimes.find(
        (c) => Number(c.id_users) === Number(caregiverId),
      );
      return ct?.clock_out ? this.formatClockTime(ct.clock_out) : null;
    },

    getCaregiverDuration(caregiverId) {
      const clockTimes = this.groupCaregiverClockTimes;
      if (!clockTimes.length) return null;
      const ct = clockTimes.find(
        (c) => Number(c.id_users) === Number(caregiverId),
      );
      if (!ct?.clock_in || !ct?.clock_out) return null;

      try {
        const clockIn = new Date(ct.clock_in);
        const clockOut = new Date(ct.clock_out);
        if (isNaN(clockIn.getTime()) || isNaN(clockOut.getTime())) return null;

        let diffMs = clockOut.getTime() - clockIn.getTime();
        if (diffMs < 0) {
          const nextDay = new Date(clockOut);
          nextDay.setDate(nextDay.getDate() + 1);
          diffMs = nextDay.getTime() - clockIn.getTime();
        }

        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        if (hours === 0) return `${minutes}m`;
        if (minutes === 0) return `${hours}h`;
        return `${hours}h ${minutes}m`;
      } catch {
        return null;
      }
    },

    parseActivities(data) {
      if (!data) return [];
      if (Array.isArray(data)) return data;
      if (typeof data === "string") {
        try {
          const parsed = JSON.parse(data);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return data.includes(",") ? data.split(",").map((s) => s.trim()) : [data];
        }
      }
      return [];
    },

    getClientDetailName(clientDetail) {
      if (clientDetail.client) {
        const c = clientDetail.client;
        return c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unknown Client";
      }
      // Fallback: try to find the client name from groupClients
      const clientId = clientDetail.id_clients;
      if (clientId) {
        const client = this.groupClients.find(c => Number(c.id) === Number(clientId));
        if (client) {
          return client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unknown Client";
        }
      }
      return "Unknown Client";
    },

    getTaskCaregiverName(caregiverId) {
      if (!caregiverId) return "Unknown";
      const caregivers = this.groupCaregivers;
      if (!caregivers.length) return "Unknown";
      const caregiver = caregivers.find(
        (c) => Number(c.id) === Number(caregiverId),
      );
      if (caregiver) {
        return caregiver.name || `${caregiver.first_name || ""} ${caregiver.last_name || ""}`.trim() || "Unknown";
      }
      return "Unknown";
    },

    async fetchMedicationLogs() {
      if (!this.shift || !this.shift.id) return;
      try {
        const response = await shiftsService.getShiftTasks(this.shift.id);
        const shiftData = response.data || response;
        if (shiftData.medicationAdministrationLogs) {
          const logsByClient = {};
          for (const log of shiftData.medicationAdministrationLogs) {
            const clientId = log.id_clients;
            if (!logsByClient[clientId]) logsByClient[clientId] = [];
            logsByClient[clientId].push(log);
          }
          this.medicationLogs = logsByClient;
        }
      } catch (err) {
        // silently handle error
      }
    },

    getClientMedicationLogs(clientDetail) {
      const clientId = clientDetail.id_clients || clientDetail.client?.id;
      return this.medicationLogs[clientId] || [];
    },

    formatClockTime(dateTimeString) {
      if (!dateTimeString) return "";

      const localTime = this.convertUTCDateTimeToLocalTime(dateTimeString);

      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    },

    convertUTCDateTimeToLocalTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";

      const utcDate = new Date(utcDateTimeStr);
      if (isNaN(utcDate.getTime())) return "";

      return utcDate.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatTime(timeString) {
      if (!timeString) return "";

      const localTimeString = this.convertUTCTimeToLocalTime(timeString);
      if (!localTimeString) return "";

      const timeParts = localTimeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";

      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;

      const formattedMinutes = minutes === "00" ? "00" : minutes;
      return `${hours}:${formattedMinutes}${ampm}`;
    },

    convertUTCTimeToLocalTime(timeStr) {
      if (!timeStr || !this.shift?.date) return timeStr;

      try {
        const shiftDate = this.shift.date.split("T")[0];
        const utcDateTime = `${shiftDate}T${timeStr}Z`;
        const utcDate = new Date(utcDateTime);

        if (isNaN(utcDate.getTime())) {
          return timeStr;
        }

        return utcDate.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return timeStr;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      // Handle date string to avoid timezone issues
      let dateStr = dateString;
      if (dateStr.includes("T")) {
        dateStr = dateStr.split("T")[0]; // Extract just the date part
      }

      // Create date object with explicit local timezone to avoid UTC interpretation
      const [year, month, day] = dateStr.split("-");
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      return dateObj.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes("T")) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as local time
      if (date) {
        const dateOnly = date.split("T")[0];
        const localDateTime = `${dateOnly}T${timeStr}`;
        const localDate = new Date(localDateTime);

        if (!isNaN(localDate.getTime())) {
          return localDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")}${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes("T")) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date && startTime && endTime) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      // Fallback: format as simple time strings
      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatStatus(status) {
      if (!status) return "";
      return status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
    },

    formatClient(shift) {
      if (!shift) return "";
      if (shift.clients && shift.clients.length > 0) {
        return shift.clients[0].name;
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      if (shift.caregivers && shift.caregivers.length > 0) {
        return shift.caregivers[0].name;
      }
      return shift.caregiver || "Unassigned";
    },

    getStatusClass(status) {
      if (!status) return "badge-secondary";

      const normalizedStatus = status.toLowerCase().replace(/\s+/g, "-");

      switch (normalizedStatus) {
        case "unassigned":
          return "badge-secondary";
        case "scheduled":
          return "badge-info";
        case "clocked-in":
        case "clocked in":
          return "badge-success";
        case "clocked-out":
        case "clocked out":
          return "badge-warning";
        case "approved":
          return "badge-primary";
        case "available":
          return "badge-success";
        case "missed":
          return "badge-secondary";
        default:
          return "badge-info";
      }
    },

    formatServiceCode(shift) {
      if (!shift) return "Not specified";

      // Check for non-service codes first (for Non Service shifts)
      const nonServiceCodes =
        shift.non_service_codes ||
        shift.nonServiceCodes ||
        shift.originalData?.non_service_codes;
      if (nonServiceCodes && nonServiceCodes.length > 0) {
        const codes = nonServiceCodes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      // Check direct service_codes array
      if (shift.service_codes && shift.service_codes.length > 0) {
        const codes = shift.service_codes.map((sc) => sc.code).filter(Boolean);
        return codes.length > 0 ? codes.join(", ") : "Not specified";
      }

      return "Not specified";
    },

    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats
      const clockIn =
        shift.originalData?.clock_in ||
        shift.originalData?.clockIn ||
        shift.actualStartTime;
      const clockOut =
        shift.originalData?.clock_out ||
        shift.originalData?.clockOut ||
        shift.actualEndTime;

      if (!clockIn && !clockOut) {
        return "Not clocked in";
      }

      const clockInTime = clockIn ? this.formatClockTime(clockIn) : "";
      const clockOutTime = clockOut ? this.formatClockTime(clockOut) : "";

      if (clockIn && clockOut) {
        return `${clockInTime} - ${clockOutTime}`;
      } else if (clockIn) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },
  },
};
</script>

<style scoped>
/* Mileage approval controls */
.mileage-approval {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 0.6rem;
}

.mileage-approval-row {
  margin-bottom: 0.5rem;
}

.mileage-approval-miles {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.35rem 0 0 1.6rem;
}

.mileage-approval-miles label {
  margin: 0;
  font-size: 0.85rem;
}

.mileage-miles-input {
  width: 110px;
}

.mileage-adjusted-note {
  color: #e6a23c;
  font-size: 0.82rem;
}

.mileage-held-note {
  margin: 0.25rem 0 0 1.6rem;
  color: #e6a23c;
  font-size: 0.82rem;
}

/* Period allowance meter under each trip row */
.mileage-allowance {
  margin: 0.3rem 0 0 1.6rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
}

.mileage-allowance.allowance-warn {
  color: #e6a23c;
}

.mileage-allowance.allowance-over {
  color: #f56c6c;
}

/* Auth warning banner */
.auth-warning-banner {
  margin: 1rem 0;
  padding: 0 2rem;
}
.auth-warning-banner .alert-warning {
  background-color: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.4);
  color: #ffc107;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.auth-warning-banner .alert-warning i {
  margin-top: 2px;
}
.auth-acknowledge-checkbox {
  color: #e0e0e0 !important;
  margin-top: 0.25rem;
}

.approve-shift-content {
  max-width: 700px;
  min-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
}

.approve-shift-content .mb-3 {
  margin-bottom: 1.5rem;
}

.approve-shift-content .mb-4 {
  margin-bottom: 2rem;
}

.approve-shift-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.approve-shift-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.approve-shift-content strong {
  color: #fff;
  font-weight: 600;
}

.notes-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
}

.notes-section p {
  color: #e0e0e0;
  margin: 0;
  line-height: 1.5;
  white-space: pre-wrap;
}

.confirmation-section {
  margin-top: 1.5rem;
}

.alert {
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.alert-info {
  background: rgba(52, 144, 220, 0.1);
  border-color: rgba(52, 144, 220, 0.3);
  color: #e0e0e0;
}

.alert i {
  color: rgba(52, 144, 220, 0.8);
  font-size: 1.1rem;
}

.text-center {
  text-align: center;
}

.status-badge {
  display: inline-block !important;
  padding: 0.35rem 0.75rem !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  border-radius: 1rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.status-badge.badge-secondary {
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.7),
    rgba(220, 53, 69, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-info {
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-success {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  color: #000 !important;
}

.status-badge.badge-warning {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-primary {
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ) !important;
  color: #fff !important;
}

.activities-section {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  padding: 1rem;
}

.activity-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.activity-tag {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: linear-gradient(
    135deg,
    rgba(52, 144, 220, 0.8),
    rgba(52, 144, 220, 0.6)
  );
  color: #fff;
  border-radius: 1.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(52, 144, 220, 0.3);
  text-align: center;
  line-height: 1.2;
}

.activity-tag.primary-activity {
  background: linear-gradient(
    135deg,
    rgba(40, 167, 69, 0.8),
    rgba(40, 167, 69, 0.6)
  );
  border: 1px solid rgba(40, 167, 69, 0.3);
}

.activity-tag.secondary-activity {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.8),
    rgba(255, 193, 7, 0.6)
  );
  color: #000;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.ai-summary-section {
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.3);
  border-radius: 0.5rem;
  padding: 1rem;
}

.ai-summary-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: rgba(156, 39, 176, 0.9);
  font-size: 0.9rem;
  font-weight: 600;
}

.ai-summary-header i {
  font-size: 1.1rem;
}

.ai-summary-section p {
  color: #e0e0e0 !important;
  margin: 0 !important;
  line-height: 1.6;
  font-style: italic;
}

/* Editable textarea styling */
:deep(.el-textarea__inner) {
  background-color: #656565 !important;
  border: 1px solid #bdbdbd !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-textarea__inner):focus {
  border-color: #ffffff !important;
  box-shadow: none !important;
}

:deep(.el-textarea__inner::placeholder) {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Caregiver Clock Times */
.caregiver-clock-times {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.caregiver-clock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.caregiver-name {
  font-weight: 600;
  color: #fff;
  min-width: 140px;
}

.caregiver-times {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.clock-label {
  color: #909399;
  font-size: 0.85rem;
}

.clock-value {
  color: #e0e0e0;
  margin-right: 0.75rem;
}

.caregiver-duration {
  background: rgba(107, 166, 205, 0.2);
  color: #6ba6cd;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}

.caregiver-locations {
  flex-basis: 100%;
  margin-top: 0.25rem;
}

.clock-location-row {
  font-size: 0.9rem;
}

.clock-map-link {
  color: #6ba6cd;
  margin-left: 0.35rem;
  white-space: nowrap;
}

.clock-map-link:hover {
  text-decoration: underline;
}

/* Client Tasks Completed */
.client-tasks-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.client-task-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
}

.client-task-header {
  font-weight: 600;
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.client-task-rows {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.client-task-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.client-task-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff;
}

.client-task-info i {
  font-size: 14px;
  color: #909399;
  width: 18px;
  text-align: center;
}

.client-task-label {
  font-weight: 500;
  font-size: 0.9rem;
}

.client-task-completed {
  color: #67c23a;
  font-size: 0.85rem;
}

.client-task-pending {
  color: #909399;
  font-size: 0.85rem;
  font-style: italic;
}

.client-notes-details {
  width: 100%;
  padding: 0.75rem;
  margin-top: 0.5rem;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  border-left: 3px solid #6ba6cd;
}

.client-notes-field {
  margin-bottom: 0.5rem;
}

.client-notes-field:last-child {
  margin-bottom: 0;
}

.client-notes-field strong {
  font-size: 0.85rem;
  color: #b0b0b0;
  display: block;
  margin-bottom: 0.25rem;
}

.notes-text {
  margin: 0;
  color: #e0e0e0;
  font-size: 0.9rem;
  white-space: pre-wrap;
}

/* Question/Answer Display */
.question-answer-row {
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.question-answer-row:last-child {
  border-bottom: none;
}

.question-text {
  color: #b0b0b0;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  font-style: italic;
}

.answer-text {
  color: #e0e0e0;
  font-size: 0.9rem;
  padding-left: 0.5rem;
}

.answer-text.answer-yes {
  color: #e0e0e0;
  font-weight: 500;
  text-transform: capitalize;
}

.answer-text.answer-no {
  color: #e0e0e0;
  font-weight: 500;
  text-transform: capitalize;
}

@media (max-width: 768px) {
  .approve-shift-content {
    min-width: auto !important;
    padding: 0 1rem;
  }

  .activity-tags {
    gap: 0.25rem;
  }

  .activity-tag {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }

  .caregiver-clock-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .caregiver-times {
    flex-wrap: wrap;
  }

  .client-task-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
}

.side-caregiver-hours-row {
  margin-bottom: 4px;
}

.side-hours-note {
  color: #b9b9b9;
  font-size: 0.85em;
  margin-left: 4px;
}
</style>
